import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSettings } from '@/hooks/useProducts';
import { useQuickEdit } from '@/hooks/useQuickEdit';
import { SITE_SHORT_NAME, IS_LODGING } from '@/config/site';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { useAuthStore } from '@/stores/auth-store';
import { useSiteSettingMutation } from '@/hooks/useSiteSettingMutation';

export interface FooterLink {
  label: string;
  href?: string;
  axissOpen?: boolean;
  axissView?: 'home' | 'chat';
}

/** Footer's brand column is narrow; longer about text is cut at a word and linked. */
export const FOOTER_ABOUT_MAX = 140;

export function truncateAtWord(text: string, max: number): string {
  if (text.length <= max) return text;
  const slice = text.slice(0, max);
  const lastSpace = slice.lastIndexOf(' ');
  return (lastSpace > 0 ? slice.slice(0, lastSpace) : slice).trimEnd() + '…';
}

/**
 * Everything a footer variant renders, derived the one way the storefront
 * derives it: the tenant's link settings (or the site-type defaults), the
 * about text and its truncation, socials, the legal entity line, the support
 * phone, the newsletter copy and the value props bar. Extracted from
 * Footer.tsx so other footer variants read the same data.
 */
export function useFooterData() {
  const { t } = useTranslation(['footer', 'nav', 'common']);
  const { data: settingsData } = useSettings();
  const { isActive } = useQuickEdit();
  // One save path for every quick-edit field on this component: the hook
  // owns the cache invalidation, so it cannot be forgotten at a call site.
  const settingMutation = useSiteSettingMutation();
  const saveSetting = (settings: Record<string, unknown>) =>
    settingMutation.mutateAsync(settings);
  const { isEnabled } = useFeatureFlags();
  const location = useLocation();
  const showEcommerce = isEnabled('ecommerce');
  const showCourses = isEnabled('courses');
  const settings = settingsData?.settings;
  const user = useAuthStore((s) => s.user);
  // The homepage renders value props as its own section, so skip the footer
  // bar there to avoid showing the same four cards twice. Also skip when
  // the tenant has enabled `hide_value_props_when_authed` and the visitor
  // is signed in — used by SaaS sites whose authed users have the app
  // surface and don't need marketing differentiators on every page.
  const isHome = location.pathname === '/';
  const hideValuePropsForAuthed = !!user && !!settings?.hide_value_props_when_authed;
  const valuePropositions = isHome || hideValuePropsForAuthed ? undefined : settings?.value_propositions;
  const footerAbout = settings?.footer_about_text;
  const storeName = settings?.store_name || SITE_SHORT_NAME;

  /**
   * Legal operating entity, shown beneath the copyright when the tenant sets
   * `legal_entity` in site_settings.
   *
   * Carrier vetting (toll-free verification, A2P 10DLC) checks that the
   * business name on the filing actually appears on the site it names. A brand
   * trading under an assumed name has nothing tying it to the LLC that filed,
   * so a reviewer landing on the site sees no connection and the filing reads
   * as unverifiable. Wording is deliberately generic: "operated by" is true
   * both for a DBA brand and for a site whose brand IS the entity.
   *
   * Optional — sites that have no separate operating entity leave it unset and
   * the line is omitted entirely.
   */
  const legalEntity =
    typeof settings?.legal_entity === 'string' ? settings.legal_entity.trim() : '';
  const legalEntityLine = legalEntity ? ` Operated by ${legalEntity}.` : '';

  /**
   * Support phone, when the tenant has one. Rendered as tel: because the
   * overwhelming majority of visitors who want a phone number are holding a
   * phone. A bare ten-digit result is a US number written the way Americans
   * write one; anything that already carries a `+`, or is any other length,
   * is left exactly as written.
   */
  const storePhone =
    typeof settings?.store_phone === 'string' ? settings.store_phone.trim() : '';
  const storePhoneDigits = storePhone.replace(/[^\d+]/g, '');
  const storePhoneHref = /^\d{10}$/.test(storePhoneDigits)
    ? `+1${storePhoneDigits}`
    : storePhoneDigits;

  const footerAboutFull =
    footerAbout || `Welcome to ${storeName}. ${showEcommerce ? 'Quality products and great service.' : 'Start your learning journey today.'}`;
  const footerAboutNeedsTruncation = !isActive && footerAboutFull.length > FOOTER_ABOUT_MAX;
  const footerAboutDisplay = footerAboutNeedsTruncation ? truncateAtWord(footerAboutFull, FOOTER_ABOUT_MAX) : footerAboutFull;

  const defaultShopLinks: FooterLink[] = IS_LODGING
    ? [
        { label: t('nav:stays', { defaultValue: 'All Properties' }), href: '/properties' },
        { label: t('nav:findReservation', { defaultValue: 'Find My Reservation' }), href: '/reservations' },
        { label: t('nav:blog'), href: '/blog' },
      ]
    : showEcommerce
    ? [
        { label: t('footer:shop.allProducts'), href: '/shop' },
        { label: t('footer:shop.newArrivals'), href: '/shop?sort=newest' },
        { label: t('footer:shop.bundles'), href: '/bundles' },
        { label: t('footer:shop.giftCards'), href: '/gift-cards' },
        { label: t('nav:blog'), href: '/blog' },
      ]
    : showCourses
    ? [
        { label: t('footer:shop.courses'), href: '/courses' },
        { label: t('footer:shop.pricing'), href: '/pricing' },
        { label: t('nav:blog'), href: '/blog' },
      ]
    : [{ label: t('nav:blog'), href: '/blog' }];

  const defaultSupportLinks: FooterLink[] = [
    { label: t('nav:myAccount'), href: '/account' },
    { label: t('footer:company.about'), href: '/page/about' },
    { label: t('footer:support.liveChat'), axissOpen: true, axissView: 'chat' },
  ];

  // Settings come from /api/settings/public OR a stale localStorage cache that
  // is shared across tenants (key 'rumio-settings-cache'). A bad shape there used
  // to crash render with `S.map is not a function`. Guard against non-arrays.
  const shopLinks: FooterLink[] = Array.isArray(settings?.footer_shop_links)
    ? settings.footer_shop_links
    : defaultShopLinks;
  const shopSectionTitle = showEcommerce
    ? t('footer:shop.heading')
    : showCourses
      ? t('footer:shop.learn')
      : t('footer:shop.explore');
  const supportLinks: FooterLink[] = Array.isArray(settings?.footer_support_links)
    ? settings.footer_support_links
    : defaultSupportLinks;
  const newsletterTitle = settings?.footer_newsletter_title || t('footer:newsletter.heading');
  const newsletterSubtitle = settings?.footer_newsletter_subtitle || t('footer:newsletter.description');

  // Social links from individual setting keys
  const facebookUrl = settings?.facebook_url as string | undefined;
  const instagramUrl = settings?.instagram_url as string | undefined;
  const twitterUrl = settings?.twitter_url as string | undefined;
  const hasSocials = !!(facebookUrl || instagramUrl || twitterUrl);

  return {
    t,
    settings,
    isActive,
    saveSetting,
    showEcommerce,
    showCourses,
    storeName,
    valuePropositions,
    legalEntityLine,
    storePhone,
    storePhoneHref,
    footerAboutFull,
    footerAboutDisplay,
    footerAboutNeedsTruncation,
    shopLinks,
    shopSectionTitle,
    supportLinks,
    newsletterTitle,
    newsletterSubtitle,
    facebookUrl,
    instagramUrl,
    twitterUrl,
    hasSocials,
  };
}

export type FooterData = ReturnType<typeof useFooterData>;
